import { useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowUpRight,
  Check,
  ChevronDown,
  CircleHelp,
  CheckCircle2,
  Download,
  Film,
  Heart,
  Image as ImageIcon,
  Library,
  LoaderCircle,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import type { Wallpaper, PaymentMode, AccountUser } from '../types';
import type { AppPage } from '../hooks/useAppNavigation';
import type { DownloadFormat } from '../lib/downloadWallpaper';
import { PageLink } from './PageLink';

export function PersonalCollectionPage({
  page,
  user,
  favoriteSyncing,
  onRefresh,
  items,
  favorites,
  paymentMode,
  loading,
  error,
  navigate,
  onOpen,
  onRemoveFavorite,
  onDownload,
  onRetry,
  onHelp,
}: {
  page: Exclude<AppPage, 'home'>;
  user: AccountUser;
  favoriteSyncing: boolean;
  onRefresh: () => void;
  items: Wallpaper[];
  favorites: string[];
  paymentMode: PaymentMode;
  loading: boolean;
  error: string;
  navigate: (href: string) => void;
  onOpen: (item: Wallpaper) => void;
  onRemoveFavorite: (item: Wallpaper) => void;
  onDownload: (item: Wallpaper, format: DownloadFormat) => Promise<void>;
  onRetry: () => void;
  onHelp: () => void;
}) {
  const [query, setQuery] = useState('');
  const [type, setType] = useState('all');
  const [sort, setSort] = useState('default');
  const isFavorites = page === 'favorites';
  const title = isFavorites ? '我的收藏' : '我拥有的';
  const favoriteItems = items.filter((item) => favorites.includes(item.id));
  const libraryItems = items.filter((item) => item.owned);
  const source = isFavorites ? favoriteItems : libraryItems;
  const results = useMemo(() => {
    const keyword = query.trim().toLocaleLowerCase();
    const list = source.filter(
      (item) =>
        (type === 'all' || item.type === type) &&
        (!keyword ||
          `${item.title} ${item.english} ${item.category}`.toLocaleLowerCase().includes(keyword)),
    );
    if (sort === 'name') list.sort((a, b) => a.title.localeCompare(b.title, 'zh-CN'));
    else if (isFavorites) list.sort((a, b) => favorites.indexOf(b.id) - favorites.indexOf(a.id));
    return list;
  }, [source, query, type, sort, isFavorites, favorites]);
  const Icon = isFavorites ? Heart : Library;
  const hasFilters = Boolean(query.trim()) || type !== 'all';
  function clearFilters() {
    setQuery('');
    setType('all');
  }
  return (
    <div className="personal-layout page-width">
      <aside className="personal-sidebar">
        <PageLink className="personal-back" href="/#discover" navigate={navigate}>
          <ArrowLeft size={17} />
          返回发现
        </PageLink>
        <div className="personal-identity">
          <span className="account-avatar">{Array.from(user.displayName)[0]?.toUpperCase()}</span>
          <div>
            <strong>{user.displayName}</strong>
            <span>{user.email}</span>
          </div>
        </div>
        <div className="personal-sidebar-label">我的空间</div>
        <nav className="personal-sections" aria-label="个人空间导航">
          <PageLink
            href="/favorites"
            navigate={navigate}
            aria-current={isFavorites ? 'page' : undefined}
          >
            <Heart size={20} />
            <span>
              我的收藏<small>留住喜欢的灵感</small>
            </span>
            <span className="personal-nav-count">{favoriteItems.length}</span>
          </PageLink>
          <PageLink
            href="/owned"
            navigate={navigate}
            aria-current={!isFavorites ? 'page' : undefined}
          >
            <Library size={20} />
            <span>
              我拥有的<small>已解锁，随时下载</small>
            </span>
            <span className="personal-nav-count">{libraryItems.length}</span>
          </PageLink>
        </nav>
        <div className="personal-sidebar-note">
          <CircleHelp size={18} />
          <p>
            喜欢可收藏，拥有可下载。
            <br />
            记录随账号保存。
          </p>
          <button onClick={onHelp}>
            下载与保存说明 <ArrowUpRight size={15} />
          </button>
        </div>
      </aside>
      <section className="personal-content" aria-labelledby="personal-title">
        <header className="personal-heading">
          <div>
            <p className="personal-breadcrumb">
              我的空间 <span>/</span> {title}
            </p>
            <h1 id="personal-title">
              {title}
              <span className="personal-total">{source.length}</span>
            </h1>
            <p className="personal-description">
              {isFavorites
                ? '把喜欢留在这里，想换壁纸时随时回来。'
                : '已解锁的作品都在这里，喜欢的画面可以再次下载。'}
            </p>
          </div>
          <span className="personal-heading-icon" aria-hidden="true">
            <Icon size={47} strokeWidth={1.25} />
          </span>
        </header>
        <div className="account-bridge is-signed-in">
          {favoriteSyncing ? (
            <LoaderCircle size={20} className="spin" />
          ) : (
            <CheckCircle2 size={20} />
          )}
          <div>
            <strong>{favoriteSyncing ? '正在保存收藏…' : '已与账号关联'}</strong>
            <p>在其他设备登录同一账号，即可查看收藏与已拥有作品。</p>
          </div>
          <button onClick={onRefresh} disabled={loading || favoriteSyncing}>
            <RefreshCw size={15} />
            刷新
          </button>
        </div>
        <div className="personal-toolbar">
          <div className="personal-type-tabs" role="group" aria-label={`${title}类型筛选`}>
            {[
              { value: 'all', label: '全部作品' },
              { value: 'static', label: '静态' },
              { value: 'dynamic', label: '动态' },
            ].map((tab) => (
              <button
                key={tab.value}
                aria-pressed={type === tab.value}
                onClick={() => setType(tab.value)}
              >
                {tab.label}
                <span>
                  {tab.value === 'all'
                    ? source.length
                    : source.filter((item) => item.type === tab.value).length}
                </span>
              </button>
            ))}
          </div>
          <div className="personal-tools">
            <label className="personal-search">
              <Search size={17} />
              <input
                aria-label={`搜索${title}`}
                placeholder={isFavorites ? '搜索收藏的作品' : '搜索已拥有作品'}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              {query && (
                <button aria-label="清空个人作品搜索" onClick={() => setQuery('')}>
                  <X size={15} />
                </button>
              )}
            </label>
            <label className="personal-sort">
              <select
                aria-label={`${title}排序`}
                value={sort}
                onChange={(event) => setSort(event.target.value)}
              >
                <option value="default">{isFavorites ? '最近收藏' : '默认排序'}</option>
                <option value="name">名称排序</option>
              </select>
              <ChevronDown size={15} />
            </label>
          </div>
        </div>
        {hasFilters && (
          <div className="personal-filter-summary">
            <span role="status">找到 {results.length} 幅作品</span>
            <button onClick={clearFilters}>
              清除筛选 <X size={14} />
            </button>
          </div>
        )}
        {loading ? (
          <div className="personal-grid" aria-label="正在加载个人作品" aria-busy="true">
            {Array.from({ length: 3 }, (_, index) => (
              <div className="personal-skeleton" key={index}>
                <div />
                <span />
                <span />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="personal-empty" role="alert">
            <CircleHelp size={38} />
            <h2>暂时无法打开{isFavorites ? '收藏' : '已拥有作品'}</h2>
            <p>{error}</p>
            <button className="button button-dark" onClick={onRetry}>
              <RefreshCw size={17} />
              重新加载
            </button>
          </div>
        ) : source.length === 0 ? (
          <div className="personal-empty">
            <span className="personal-empty-icon">
              <Icon size={40} strokeWidth={1.4} />
            </span>
            <h2>{isFavorites ? '还没有收藏的作品' : '从第一幅拥有的作品开始'}</h2>
            <p>
              {isFavorites
                ? '遇到喜欢的壁纸，点击爱心就能留在这里。'
                : '解锁喜欢的作品后，可以在这里再次下载原图和视频。'}
            </p>
            <PageLink className="button button-dark" href="/#discover" navigate={navigate}>
              去发现壁纸 <ArrowUpRight size={18} />
            </PageLink>
            {!isFavorites && (
              <span className="personal-empty-caption">收藏的作品不等于已拥有作品。</span>
            )}
          </div>
        ) : results.length === 0 ? (
          <div className="personal-empty">
            <Search size={35} />
            <h2>没有找到匹配的作品</h2>
            <p>试试其他关键词，或清除筛选查看全部{isFavorites ? '收藏' : '已拥有作品'}。</p>
            <button className="button button-outline" onClick={clearFilters}>
              查看全部{isFavorites ? '收藏' : '已拥有作品'}
            </button>
          </div>
        ) : (
          <div className="personal-grid">
            {results.map((item) => (
              <PersonalCard
                key={item.id}
                item={item}
                isFavorites={isFavorites}
                paymentMode={paymentMode}
                onOpen={() => onOpen(item)}
                onRemove={() => onRemoveFavorite(item)}
                onDownload={(format) => onDownload(item, format)}
              />
            ))}
          </div>
        )}
        {!loading && !error && source.length > 0 && (
          <div className="personal-page-note">
            <CircleHelp size={16} />
            <p>
              {isFavorites
                ? '收藏已保存到你的账号，取消收藏不会影响已拥有作品。'
                : '这些作品属于当前账号，登录同一账号即可再次下载。请妥善保存下载的文件。'}
              {!isFavorites &&
                source.some((item) => item.type === 'dynamic') &&
                ' 动态作品提供 MP4 素材，不含原生 Live Photo。'}
            </p>
            <button onClick={onHelp}>了解更多</button>
          </div>
        )}
      </section>
    </div>
  );
}

function PersonalCard({
  item,
  isFavorites,
  paymentMode,
  onOpen,
  onRemove,
  onDownload,
}: {
  item: Wallpaper;
  isFavorites: boolean;
  paymentMode: PaymentMode;
  onOpen: () => void;
  onRemove: () => void;
  onDownload: (format: DownloadFormat) => Promise<void>;
}) {
  const [busy, setBusy] = useState<DownloadFormat | null>(null);
  const [error, setError] = useState('');
  async function download(format: DownloadFormat) {
    setBusy(format);
    setError('');
    try {
      await onDownload(format);
    } catch (error) {
      setError((error as Error).message);
    } finally {
      setBusy(null);
    }
  }
  const status = item.owned
    ? item.price === 0
      ? '免费领取'
      : paymentMode === 'demo'
        ? '体验已解锁'
        : '已解锁'
    : item.price === 0
      ? '免费作品'
      : `未解锁 · ¥${item.price}`;
  return (
    <article className="personal-card" data-artwork={item.id}>
      <button className="personal-art" onClick={onOpen} aria-label={`预览${item.title}`}>
        <img src={`/art/${item.id}.jpg`} alt={item.title} loading="lazy" />
        <span className="personal-art-badge">
          {item.type === 'dynamic' ? <Film size={14} /> : <ImageIcon size={14} />}
          {item.type === 'dynamic' ? '动态 + 静态' : '静态原图'}
        </span>
        <span className="personal-art-open">
          <ArrowUpRight size={19} />
          <span>预览作品</span>
        </span>
      </button>
      <div className="personal-card-body">
        <div className="personal-card-heading">
          <button onClick={onOpen}>{item.title}</button>
          <span className={`personal-status ${item.owned ? 'is-owned' : ''}`}>
            {item.owned && <Check size={13} />}
            {status}
          </span>
        </div>
        <p className="personal-card-category">
          {item.category}
          <span>·</span>
          {item.type === 'dynamic' ? 'PNG / MP4' : 'PNG'}
        </p>
        <div className="personal-card-actions">
          {isFavorites ? (
            <>
              <button className="personal-preview-button" onClick={onOpen}>
                查看作品 <ArrowUpRight size={16} />
              </button>
              <button
                className="personal-remove"
                onClick={onRemove}
                aria-label={`取消收藏${item.title}`}
              >
                <Heart size={15} />
                取消收藏
              </button>
            </>
          ) : (
            <>
              <button
                className="personal-download"
                disabled={Boolean(busy)}
                onClick={() => download('png')}
                aria-label={`下载${item.title}原图`}
              >
                {busy === 'png' ? (
                  <LoaderCircle size={16} className="spin" />
                ) : (
                  <Download size={16} />
                )}
                下载原图
              </button>
              {item.type === 'dynamic' && (
                <button
                  className="personal-download secondary"
                  disabled={Boolean(busy)}
                  onClick={() => download('mp4')}
                  aria-label={`下载${item.title}视频`}
                >
                  {busy === 'mp4' ? (
                    <LoaderCircle size={16} className="spin" />
                  ) : (
                    <Film size={16} />
                  )}
                  MP4
                </button>
              )}
            </>
          )}
        </div>
        {error && (
          <p className="personal-card-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </article>
  );
}
